import { inject, Injectable, signal, Signal } from '@angular/core';
import { StylesResponse } from '../../dto/styles-response';
import { StoreService } from '../store/store.service';
import { HttpClient } from '@angular/common/http';
import { ApiResponse } from '../../dto/api-response';
import { OPE_STYLES_GET } from '../../constants/endpoints';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { Styles } from '../../models/styles';
import { handleGenericHttpError } from '../../utils/http-error.util';

const defaultStyles: Styles = {
  brand: {
    logoUrl: "https://www.cubcadet.com/on/demandware.static/Sites-cubcadet-Site/-/default/dw0a1ac07f/images/svg-icons/logo.svg",
    color: "#FBCE28",
  },
  actionButton: {
    color: "#000000",
    background: "#EDC245",
    border: "none",
  },
  spinner: {
    color: "#000000"
  }
}

@Injectable({
  providedIn: 'root'
})
export class StyleService {
  // Injected dependencies
  private store = inject(StoreService);
  private http = inject(HttpClient);

  // Create observable from store signal
  private supplierId$ = toObservable(this.store.supplierId);

  // Subscribe to supplier id changes to fetch styles
  private styles$ = this.supplierId$.pipe(
    switchMap((supplierId: string | null) => {
      if (!supplierId || supplierId.trim() === '' || supplierId === "default") {
        this.loadingSig.set(false);
        return of(defaultStyles);
      }

      return this.fetchStyles(supplierId);
    }),
  );

  public readonly styles: Signal<Styles> = toSignal(this.styles$, { initialValue: defaultStyles });

  // Loading signal
  private loadingSig = signal<boolean>(true);
  public readonly isLoading = this.loadingSig.asReadonly();

  /**
   * Helper method to fetch styles
   * @param supplierId Supplier to fetch styles for
   * @return Styles object
   */
  private fetchStyles(supplierId: string): Observable<Styles> {
    console.debug("[STYLE-SERVICE]: fetching styles for supplier id ", supplierId);

    const url = OPE_STYLES_GET(supplierId);
    return this.http.get<ApiResponse<StylesResponse>>(url).pipe(
      map((resp) => {
        if (this.loadingSig()) this.loadingSig.set(false);

        console.debug("[STYLE-SERVICE]: obtained styles", resp.data.styles);
        return resp.data.styles;
      }),
      catchError(handleGenericHttpError),
    )
  }
}
